import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowUpRight, Sparkles, Search, Layers, Target, Globe2, Zap, GitMerge, TrendingDown } from "lucide-react";
import { Logo } from "./landing/Logo";

// ── Menu data ───────────────────────────────────────────────────
// Each top-level entry can be a "simple" link (just `to`) or a mega menu (with `columns`).

const PLATFORM = {
  label: "Platform",
  intro: "Online visibility, simplified — built for the AI search era.",
  columns: [
    {
      title: "AI search",
      links: [
        { label: "AI Citation Tracking", to: "/ai-citation-tracking", desc: "Track mentions in ChatGPT, Perplexity, Gemini, AIO." },
        { label: "GEO Optimization", to: "/geo-optimization", desc: "Optimize content to be cited by AI engines." },
        { label: "AI Visibility Checker", to: "/tools/ai-visibility-checker", desc: "Free score on any URL.", badge: "FREE" },
      ],
    },
    {
      title: "SEO core",
      links: [
        { label: "Keyword Clustering", to: "/keyword-clustering", desc: "Auto-group keywords by intent." },
        { label: "SEO Automation", to: "/seo-automation", desc: "What to automate, what to approve." },
        { label: "Topical Authority", to: "/topical-authority", desc: "Map and dominate your niche." },
        { label: "Search Intent Tool", to: "/search-intent-tool", desc: "Detect intent for any keyword." },
      ],
    },
    {
      title: "Content",
      links: [
        { label: "AI SEO Services", to: "/ai-seo-services", desc: "Full-stack AI SEO — content, citations, schema, publishing." },
        { label: "AI SEO Tools", to: "/ai-seo-tools", desc: "Full Claude-powered content engine." },
        { label: "Features", to: "/features", desc: "Everything in SEOintent." },
      ],
    },
  ],
};

const SOLUTIONS = {
  label: "Solutions",
  intro: "AI SEO playbooks tailored to every business type.",
  columns: [
    {
      title: "By role",
      links: [
        { label: "For Agencies", to: "/for-agencies" },
        { label: "For Marketing Agencies", to: "/ai-seo-for-marketing-agencies" },
        { label: "For SaaS", to: "/ai-seo-for-saas" },
        { label: "For B2B", to: "/ai-seo-for-b2b" },
      ],
    },
    {
      title: "Local & service",
      links: [
        { label: "For Real Estate", to: "/ai-seo-for-real-estate" },
        { label: "For Restaurants", to: "/ai-seo-for-restaurants" },
        { label: "For Dentists", to: "/ai-seo-for-dentists" },
        { label: "For Law Firms", to: "/ai-seo-for-law-firms" },
      ],
    },
    {
      title: "More",
      links: [
        { label: "For eCommerce", to: "/ai-seo-for-ecommerce" },
        { label: "For Healthcare", to: "/ai-seo-for-healthcare" },
        { label: "For Coaches", to: "/ai-seo-for-coaches" },
      ],
    },
  ],
};

const RESOURCES = {
  label: "Resources",
  intro: "Everything you need to win in AI search and traditional SEO.",
  columns: [
    {
      title: "Learn",
      links: [
        { label: "Blog", to: "/blog" },
        { label: "Resources Hub", to: "/resources" },
        { label: "AI Tool Use Cases", to: "/use-cases", badge: "NEW" },
        { label: "SEO Guide", to: "/seo-guide" },
        { label: "GEO Guide", to: "/geo-guide" },
      ],
    },
    {
      title: "Deep dives",
      links: [
        { label: "What Is Search Intent", to: "/what-is-search-intent" },
        { label: "Search Intent Keyword Research", to: "/search-intent-keyword-research" },
        { label: "GEO vs SEO", to: "/geo-vs-seo" },
        { label: "How To Rank In ChatGPT", to: "/how-to-rank-in-chatgpt" },
        { label: "AI Citation Tracking Guide", to: "/ai-citation-tracking-guide" },
      ],
    },
    {
      title: "Free tools",
      links: [
        { label: "AI Visibility Checker", to: "/tools/ai-visibility-checker", badge: "FREE" },
        { label: "AI Content Detector", to: "/tools/ai-content-detector", badge: "NEW" },
        { label: "Schema Markup Generator", to: "/tools/schema-generator", badge: "FREE" },
        { label: "Meta Tag Analyzer", to: "/tools/meta-tag-analyzer", badge: "FREE" },
        { label: "Sitemap & Robots Analyzer", to: "/tools/sitemap-analyzer", badge: "FREE" },
      ],
    },
  ],
};

const COMPARE = {
  label: "Compare",
  intro: "How SEOintent stacks up to other SEO and AI content tools.",
  columns: [
    {
      title: "vs SEO suites",
      links: [
        { label: "vs Ahrefs", to: "/ahrefs-alternative" },
        { label: "vs Ahrefs (deep)", to: "/vs/ahrefs" },
        { label: "vs Semrush", to: "/semrush-alternative" },
        { label: "vs Semrush (deep)", to: "/vs/semrush" },
        { label: "vs Surfer SEO", to: "/surfer-seo-alternative" },
        { label: "vs Frase", to: "/vs/frase" },
        { label: "vs MarketMuse", to: "/vs/marketmuse" },
      ],
    },
    {
      title: "vs Content tools",
      links: [
        { label: "vs Clearscope", to: "/vs/clearscope" },
        { label: "vs Neuronwriter", to: "/vs/neuronwriter" },
        { label: "vs Surfer (deep)", to: "/vs/surfer-seo" },
      ],
    },
    {
      title: "AI writer alternatives",
      links: [
        { label: "Jasper Alternative", to: "/jasper-alternative" },
        { label: "Copy.ai Alternative", to: "/copy-ai-alternative" },
        { label: "Writesonic Alternative", to: "/writesonic-alternative" },
        { label: "Outranking Alternative", to: "/outranking-alternative" },
        { label: "Scalenut Alternative", to: "/scalenut-alternative" },
      ],
    },
  ],
};

const SIMPLE_LINKS = [
  { label: "Pricing", to: "/pricing" },
  { label: "Affiliates", to: "/affiliates" },
];

const MENUS = [PLATFORM, SOLUTIONS, RESOURCES, COMPARE];

// ── Sub-components ──────────────────────────────────────────────

function Badge({ children }) {
  const isNew = String(children).toUpperCase() === "NEW";
  const cls = isNew ? "bg-[#FF2F2F]/10 text-[#FF2F2F] border-[#FF2F2F]/30"
                    : "bg-emerald-50 text-emerald-700 border-emerald-200";
  return (
    <span className={`ml-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border rounded ${cls}`}>
      {children}
    </span>
  );
}

function MegaPanel({ menu, onLinkClick }) {
  return (
    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[min(calc(100vw-2rem),1100px)]">
      <div className="bg-white border border-zinc-200 rounded-2xl shadow-2xl shadow-zinc-900/10 overflow-hidden">
        {menu.intro && (
          <div className="px-8 pt-6 pb-4 border-b border-zinc-100">
            <p className="font-cabinet text-sm font-semibold text-zinc-900">
              {menu.label} <span className="text-zinc-400 font-normal">— {menu.intro}</span>
            </p>
          </div>
        )}
        <div className="grid grid-cols-3 gap-x-8 gap-y-2 px-8 py-6">
          {menu.columns.map((col, i) => (
            <div key={i}>
              <p className="font-mono-data text-[10px] uppercase tracking-wider text-zinc-400 mb-3">
                {col.title}
              </p>
              <ul className="space-y-1">
                {col.links.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      onClick={onLinkClick}
                      className="group block py-1.5 -mx-2 px-2 rounded-md hover:bg-zinc-50 transition-colors"
                    >
                      <div className="flex items-center">
                        <span className="font-satoshi text-sm font-semibold text-zinc-900 group-hover:text-[#FF2F2F] transition-colors">
                          {link.label}
                        </span>
                        {link.badge && <Badge>{link.badge}</Badge>}
                      </div>
                      {link.desc && (
                        <p className="font-satoshi text-xs text-zinc-500 mt-0.5 leading-snug">
                          {link.desc}
                        </p>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileSection({ menu, onLinkClick }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-zinc-100">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-3 px-2 text-left"
      >
        <span className="font-satoshi font-semibold text-sm text-zinc-900">{menu.label}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pb-3 px-2 space-y-3">
          {menu.columns.map((col, i) => (
            <div key={i}>
              <p className="font-mono-data text-[10px] uppercase tracking-wider text-zinc-400 mb-1.5">
                {col.title}
              </p>
              <ul className="space-y-0.5">
                {col.links.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      onClick={onLinkClick}
                      className="flex items-center py-1.5 font-satoshi text-sm text-zinc-700 hover:text-[#FF2F2F]"
                    >
                      {link.label}
                      {link.badge && <Badge>{link.badge}</Badge>}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main MegaNav ────────────────────────────────────────────────

export default function MegaNav({ ctaText = "Get Started", ctaTo = "/register", loginText = "Sign in", stickyTopClass = "top-0" }) {
  const [openIdx, setOpenIdx] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Close menu on route change
  useEffect(() => {
    setOpenIdx(null);
    setMobileOpen(false);
  }, [location.pathname]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setOpenIdx(null); setMobileOpen(false); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <nav
      className={`sticky ${stickyTopClass} z-40 bg-white/95 backdrop-blur-md border-b border-zinc-200`}
      onMouseLeave={() => setOpenIdx(null)}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3 relative">
        <Link to="/" className="flex-shrink-0"><Logo /></Link>

        {/* Desktop menu */}
        <div className="hidden lg:flex items-center gap-1 flex-1 justify-center">
          {MENUS.map((menu, i) => (
            <button
              key={menu.label}
              onMouseEnter={() => setOpenIdx(i)}
              onFocus={() => setOpenIdx(i)}
              onClick={() => setOpenIdx(openIdx === i ? null : i)}
              className={`flex items-center gap-1 px-3 py-2 rounded-lg font-satoshi text-sm font-medium transition-colors ${
                openIdx === i ? "bg-zinc-100 text-zinc-900" : "text-zinc-600 hover:text-zinc-900"
              }`}
              aria-haspopup="true"
              aria-expanded={openIdx === i}
            >
              {menu.label}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openIdx === i ? "rotate-180" : ""}`} />
            </button>
          ))}
          {SIMPLE_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="px-3 py-2 rounded-lg font-satoshi text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>

        {/* Right-side actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to="/login"
            className="hidden sm:block whitespace-nowrap text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
          >
            {loginText}
          </Link>
          <Link
            to={ctaTo}
            className="whitespace-nowrap bg-[#FF2F2F] hover:bg-[#e02828] text-white font-semibold rounded-lg transition-colors text-xs px-3 py-1.5 sm:text-sm sm:px-4 sm:py-2"
          >
            {ctaText}
          </Link>
          <button
            onClick={() => setMobileOpen(o => !o)}
            className="lg:hidden p-1.5 text-zinc-600 hover:text-zinc-900"
            aria-label="Toggle navigation"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Desktop mega panel */}
        {openIdx !== null && (
          <div className="hidden lg:block">
            <MegaPanel menu={MENUS[openIdx]} onLinkClick={() => setOpenIdx(null)} />
          </div>
        )}
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-zinc-200 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-2">
            {MENUS.map((menu) => (
              <MobileSection key={menu.label} menu={menu} onLinkClick={() => setMobileOpen(false)} />
            ))}
            {SIMPLE_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                className="block py-3 px-2 font-satoshi font-semibold text-sm text-zinc-900 border-b border-zinc-100"
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/login"
              onClick={() => setMobileOpen(false)}
              className="block py-3 px-2 font-satoshi font-semibold text-sm text-zinc-700"
            >
              {loginText}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
